<script lang="ts">
	import { EditionsPdpDescription } from './EditionsPdpDescription';
	import type { EditionsPdpData } from './utils/getPdpData/getPdpData';
	import { page } from '$app/state';
	import { Breadcrumbs } from '$global/components/Breadcrumbs';
	import { Container } from '$global/components/Container';
	import { PdpGallery } from '$global/features/pdp/components/PdpGallery';
	import { getPageData } from '$global/utils/getPageData/getPageData';

	interface Props {
		// The page's own data by default; the drop page dialog passes the data
		// of the edition it shows.
		data?: EditionsPdpData;
		// Inside a dialog: no breadcrumbs, no page container, no lightbox (it
		// would open a dialog over the dialog) and no link back to the drop
		// page the visitor is already on.
		embedded?: boolean;
		dataCy?: string;
	}

	let {
		data: dataProp = undefined,
		embedded = false,
		dataCy = 'editions-pdp',
	}: Props = $props();

	let selectedItemIndex = $state(0);
	let isLightboxOpen = $state(false);
	let galleryElement: HTMLDivElement | undefined = $state();

	// Inside the dialog there is no lightbox, so the sizing guide only swaps
	// the gallery's main image, which on a phone has scrolled out of view above
	// the details. The gallery opens the dialog's content, so scrolling the
	// dialog back to the top shows it as first laid out (and leaves a dialog
	// already at the top, e.g. on desktop, where it is).
	const showScaleImage = () =>
		galleryElement
			?.closest('[role="dialog"]')
			?.scrollTo({ top: 0, behavior: 'smooth' });

	let data = $derived(dataProp ?? getPageData<EditionsPdpData>(page.data));
	let crumbs = $derived(data.crumbs);
	let gallery = $derived(data.gallery);

	let legend = $derived(
		(() => {
			if (data.edition.series?.slug !== 'civilisation') {
				return null;
			}

			switch (selectedItemIndex) {
				case 0:
					return 'White Aluminium Frame';
				case 1:
					return 'Oak Veneer Frame';
				case 2:
					return 'Gold Effect Aluminium Frame';
				case 3:
					return 'Black Aluminium Frame';
				default:
					return null;
			}
		})()
	);
</script>

{#snippet body()}
	<div bind:this={galleryElement} class="lg:w-1/2">
		<PdpGallery
			bind:selectedItemIndex
			bind:isLightboxOpen
			{dataCy}
			{legend}
			items={gallery}
			includeLightbox={!embedded}
		/>
	</div>
	<EditionsPdpDescription
		{dataCy}
		{data}
		{embedded}
		onSizingGuide={embedded ? showScaleImage : undefined}
		class="lg:w-1/2"
		bind:selectedItemIndex
		bind:isLightboxOpen
	/>
{/snippet}

{#if embedded}
	<div data-cy={dataCy} class="lg:flex lg:gap-10">
		{@render body()}
	</div>
{:else}
	<Breadcrumbs {dataCy} {crumbs} />
	<Container {dataCy} class="mb-[8.75rem] lg:flex lg:gap-10">
		{@render body()}
	</Container>
{/if}
